import Phaser from 'phaser';
import { track } from './analytics';

// Централизованная подписка на существующие scene.events эмиты (analytics:*,
// fx:*, boss:*). НИКАКИХ новых эмитов — только слушаем то, что уже эмитится
// в GameScene. Одно место где event → track. SHUTDOWN cleanup обязателен:
// без него рестарт сцены плодит дубли слушателей.
//
// Живёт независимо от haptic-bindings: одни и те же эмиты обслуживаются двумя
// параллельными подписками (тактилка и аналитика — разные цели, не мешают).

interface LevelStartPayload {
  level: number;
}
interface LevelCompletePayload {
  level: number;
  durationMs: number;
}
interface LevelFailedPayload {
  level: number;
  killedBy: string;
  wave: number;
}
interface WeaponPurchasedPayload {
  id: string;
}

export function bindAnalytics(scene: Phaser.Scene): void {
  const handlers: Array<[string, (payload?: unknown) => void]> = [];

  // Хелпер: типизированный subscribe с автозаписью в handlers-список для cleanup.
  const subscribe = <P>(event: string, fn: (p: P) => void): void => {
    const wrapper = (raw?: unknown): void => fn(raw as P);
    scene.events.on(event, wrapper);
    handlers.push([event, wrapper]);
  };

  subscribe<LevelStartPayload>('analytics:level-start', (p) =>
    track('level_start', { level: p.level }),
  );
  subscribe<LevelCompletePayload>('analytics:level-complete', (p) =>
    track('level_complete', { level: p.level, durationMs: p.durationMs }),
  );
  subscribe<LevelFailedPayload>('analytics:level-failed', (p) =>
    track('level_failed', {
      level: p.level,
      killedBy: p.killedBy,
      wave: p.wave,
    }),
  );
  subscribe<WeaponPurchasedPayload>('fx:weapon-purchased', (p) =>
    track('weapon_purchased', { id: p.id }),
  );

  // boss:defeated сейчас эмитится без payload (см. GameScene.ts:740). Оставляем
  // { boss: undefined } — payload-поле опционально в AnalyticsEvents. Если позже
  // прокинем id босса, автоматически подхватим.
  const bossDefeatedHandler = (): void => track('boss_defeated', {});
  scene.events.on('boss:defeated', bossDefeatedHandler);
  handlers.push(['boss:defeated', bossDefeatedHandler]);

  const gameOverHandler = (): void => track('game_over', {});
  scene.events.on('fx:game-over', gameOverHandler);
  handlers.push(['fx:game-over', gameOverHandler]);

  const finalVictoryHandler = (): void => track('final_victory', {});
  scene.events.on('fx:final-victory', finalVictoryHandler);
  handlers.push(['fx:final-victory', finalVictoryHandler]);

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    for (const [event, fn] of handlers) {
      scene.events.off(event, fn);
    }
  });
}
